import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import { productsApi, userApi } from "@/lib/api";
import { SOCIAL_LINKS } from "@/data/socialLinks";
import { toast } from "sonner";
import { LogOut, Trash2, ExternalLink, Check } from "lucide-react";
import { IDS } from "@/constants/testIds";

export default function Profile() {
  const { user, signOut, refresh } = useAuth();
  const nav = useNavigate();
  const [unlocks, setUnlocks] = useState(null);

  useEffect(() => {
    productsApi.getMyUnlocks().then(setUnlocks).catch(() => {});
  }, []);

  const doLogout = async () => {
    if (!window.confirm("Sign out of Paranormal Archetypes?")) return;
    await signOut();
    nav("/login", { replace: true });
  };

  const doDelete = async () => {
    const ok = window.confirm(
      "This permanently deletes your account and personal data. This cannot be undone. Continue?"
    );
    if (!ok) return;
    try {
      await userApi.deleteAccount();
      await signOut();
      nav("/login", { replace: true });
      toast.success("Account deleted.");
    } catch (e) {
      toast.error(e.message || "Could not delete account");
    }
  };

  return (
    <div className="space-y-6" data-testid={IDS.profilePage}>
      <header className="flex items-center gap-4">
        {user?.picture ? (
          <img src={user.picture} alt={user.name} className="w-16 h-16 rounded-full object-cover" style={{ border: "1px solid var(--pa-card-border)" }} />
        ) : (
          <div className="w-16 h-16 rounded-full flex items-center justify-center pa-serif text-xl" style={{ background: "var(--pa-card)", color: "var(--pa-gold)", border: "1px solid var(--pa-card-border)" }}>
            {(user?.name || "?").slice(0, 1)}
          </div>
        )}
        <div>
          <h1 className="pa-serif text-2xl md:text-3xl" style={{ color: "var(--pa-text-primary)" }}>{user?.name}</h1>
          <p className="text-sm" style={{ color: "var(--pa-text-secondary)" }}>{user?.email}</p>
        </div>
      </header>

      {user?.is_verified && (
        <div className="pa-card p-4 flex items-center gap-2">
          <Check size={16} color="var(--pa-success)" />
          <span className="text-sm" style={{ color: "var(--pa-text-primary)" }}>Verified access</span>
        </div>
      )}

      {(user?.archetypes || []).length > 0 && (
        <section className="pa-card p-5 md:p-6">
          <h2 className="pa-serif text-lg mb-3" style={{ color: "var(--pa-gold)" }}>Your archetypes</h2>
          <ul className="space-y-2">
            {(user.archetypes || []).slice(0, 2).map((a, i) => (
              <li key={a} className="flex items-center gap-2 text-sm" style={{ color: "var(--pa-text-primary)" }}>
                <span className="text-[10px] uppercase tracking-widest" style={{ color: "var(--pa-gold)" }}>
                  {i === 0 ? "Primary" : "Secondary"}
                </span>
                <span>·</span>
                <Link to={`/archetype/${encodeURIComponent(a)}`} className="hover:underline">{a}</Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      {!user?.is_verified && (
        <section className="pa-card p-5">
          <h2 className="pa-serif text-lg mb-2" style={{ color: "var(--pa-text-primary)" }}>Enter your access code</h2>
          <p className="text-sm mb-3" style={{ color: "var(--pa-text-secondary)" }}>
            Redeem the code from your purchase confirmation email.
          </p>
          <Link to="/unlock" className="pa-btn-gold inline-block">Enter Access Code</Link>
        </section>
      )}

      {unlocks?.product_ids?.length > 0 && (
        <section className="pa-card p-5">
          <h2 className="pa-serif text-lg mb-3" style={{ color: "var(--pa-gold)" }}>Owned products</h2>
          <ul className="text-sm space-y-1" style={{ color: "var(--pa-text-primary)" }}>
            {unlocks.product_ids.map((pid) => (
              <li key={pid} className="flex items-center gap-2"><Check size={14} color="var(--pa-success)" /> {pid}</li>
            ))}
          </ul>
        </section>
      )}

      <section className="pa-card p-5">
        <h2 className="pa-serif text-lg mb-3" style={{ color: "var(--pa-gold)" }}>Follow</h2>
        <div className="flex flex-wrap gap-2">
          {SOCIAL_LINKS.map((s) => (
            <a
              key={s.key}
              href={s.url}
              target="_blank"
              rel="noopener noreferrer"
              className="pa-btn-outline text-xs inline-flex items-center gap-1"
              data-testid={`social-${s.key}`}
            >
              {s.label} <ExternalLink size={12} />
            </a>
          ))}
        </div>
      </section>

      <section className="flex flex-wrap gap-2">
        <Link to="/faq" className="pa-btn-outline text-sm">FAQ</Link>
        <Link to="/legal" className="pa-btn-outline text-sm">Legal</Link>
      </section>

      <section className="flex flex-col gap-2 pt-4">
        <button
          onClick={doLogout}
          className="pa-btn-outline inline-flex items-center gap-2 justify-center w-full sm:w-auto"
          data-testid={IDS.logoutBtn}
        >
          <LogOut size={16} /> Sign out
        </button>
        <button
          onClick={doDelete}
          className="pa-btn-ghost inline-flex items-center gap-2 justify-center text-sm mt-2"
          style={{ color: "var(--pa-error)" }}
          data-testid="delete-account-btn"
        >
          <Trash2 size={14} /> Delete account
        </button>
      </section>
    </div>
  );
}
